<template>
  <v-dialog :value="value" max-width="1000" scrollable @input="$emit('input', $event)">
    <v-card v-if="value && code">
      <v-card-title class="d-flex align-center flex-wrap" style="gap: 8px">
        <span class="mono">{{ code }}</span>
        <span v-if="data && data.key.label" class="text-body-2 text--secondary">{{ data.key.label }}</span>
        <v-spacer />
        <v-chip v-if="data && data.key.information" x-small outlined>{{ $t('charging-point-tracked-keys.information') }}<template v-if="data.key.unit"> · {{ data.key.unit }}</template></v-chip>
        <v-btn v-if="!data || !data.key.information" small color="primary" :disabled="!data || !data.present" @click="openApply">{{ $t('charging-point-tracked-keys.apply') }}</v-btn>
        <v-btn icon small :aria-label="$t('close')" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <div v-if="!data" class="text-center my-6"><v-progress-circular indeterminate color="primary" /></div>
        <template v-else>
          <p v-if="data.key.description" class="text-body-2">{{ data.key.description }}</p>
          <div class="text-caption text--secondary mb-2">
            {{ $t('charging-point-tracked-keys.scope', {assessed: data.assessed, total: data.chargingPoints}) }}
            <template v-if="data.key.expected !== null"> · {{ $t('charging-point-tracked-keys.expected-everywhere', {value: data.key.expected}) }}</template>
          </div>

          <!-- Chercher dans les valeurs (une adresse, un APN…) -->
          <v-text-field v-if="data.values.length > 6" v-model="valueSearch" :label="$t('charging-point-tracked-keys.value-search')" prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable class="mb-2" style="max-width: 360px" />
          <div v-if="boolean" class="text-caption text--secondary mb-1">{{ $t('charging-point-tracked-keys.boolean-hint') }}</div>

          <!-- Combien de bornes à chaque valeur (un booléen : par sens, ses écritures dessous) ; un clic liste les bornes -->
          <v-simple-table dense class="mb-4">
            <tbody>
              <tr v-if="!shownRows.length"><td colspan="4" class="text--secondary">{{ valueSearch ? $t('charging-point-tracked-keys.none-found') : $t('charging-point-tracked-keys.no-reading') }}</td></tr>
              <tr v-for="row in shownRows" :key="row.id" :class="{selected: selected && selected.id === row.id}" class="clickable" @click="select(row)">
                <td class="mono" style="width: 34%">
                  <span v-if="row.missing" class="text--secondary">{{ $t('charging-point-tracked-keys.missing') }}</span>
                  <span v-else-if="row.deviatingOnly" class="orange--text">{{ $t('charging-point-tracked-keys.deviating-all') }}</span>
                  <template v-else-if="row.values">
                    <span>{{ $t('charging-point-tracked-keys.truth.' + row.value) }}</span>
                    <div class="d-flex flex-wrap mt-1" style="gap: 4px">
                      <v-chip v-for="spelling in row.spellings" :key="spelling.value" x-small outlined :input-value="selected && selected.id === spelling.id" @click.stop="select(spelling)">
                        {{ spelling.value }} · {{ spelling.chargingPoints }}
                      </v-chip>
                    </div>
                  </template>
                  <span v-else>{{ display(row.value) }}</span>
                </td>
                <td><v-progress-linear :value="data.assessed ? row.chargingPoints * 100 / data.assessed : 0" height="8" rounded :color="row.missing ? 'grey' : (row.deviating ? 'orange' : 'primary')" /></td>
                <td class="text-right text-no-wrap" style="width: 1%">{{ $tc('charging-point-tracked-keys.charging-points', row.chargingPoints, {count: row.chargingPoints}) }}</td>
                <td class="text-caption text-no-wrap" style="width: 1%">
                  <span v-if="row.readonly" class="text--secondary">{{ $tc('charging-point-tracked-keys.readonly-count', row.readonly, {count: row.readonly}) }}</span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <template v-if="data.key.variants.length">
            <div class="text-subtitle-2 mb-1">{{ $t('charging-point-tracked-keys.variants') }}</div>
            <div v-for="variant in data.key.variants" :key="variant.id" class="text-caption">
              <v-chip x-small outlined class="mr-1">{{ $t('charging-point-tracked-keys.layers.' + variant.layer) }}</v-chip>{{ variant.targetName }}
              <span v-if="variant.versionPattern" class="mono"> · {{ variant.versionPattern }}</span>
              <span v-if="variant.key"> · {{ $t('charging-point-tracked-keys.named') }} <span class="mono">{{ variant.key }}</span></span>
              <span v-if="variant.expected !== null"> · {{ $t('charging-point-tracked-keys.expects') }} <span class="mono">{{ display(variant.expected) }}</span></span>
            </div>
          </template>

          <template v-if="selected">
            <div class="d-flex align-center mt-4 mb-1">
              <div class="text-subtitle-2">{{ $tc('charging-point-tracked-keys.charging-points', list ? list.total : 0, {count: list ? list.total : 0}) }}</div>
              <v-spacer />
              <v-btn small text :disabled="!list || !list.total" :loading="exporting" @click="exportCsv"><v-icon small left>mdi-download</v-icon>CSV</v-btn>
            </div>
            <v-data-table
                :headers="headers"
                :items="list ? list.rows : []"
                :loading="loadingList"
                :server-items-length="list ? list.total : 0"
                :options.sync="listOptions"
                :footer-props="{'items-per-page-options': [50, 100, 200]}"
                dense
            >
              <template #item.name="{item}">
                <router-link :to="{name: 'charging-points.detail.manage', params: {id: item.chargingPointId}}">{{ item.name || item.identifier || item.chargingPointId }}</router-link>
                <v-icon x-small :color="item.connected ? 'success' : 'grey'" class="ml-1">mdi-circle</v-icon>
              </template>
              <template #item.value="{item}">
                <span class="mono">{{ item.present ? display(item.value) : $t('charging-point-tracked-keys.missing') }}</span>
                <span v-if="item.key !== code" class="text-caption text--secondary mono"> ({{ item.key }})</span>
              </template>
              <template #item.type="{item}">{{ [item.manufacturerName, item.productLineName].filter(v => v).join(' · ') }}</template>
            </v-data-table>
          </template>
        </template>
      </v-card-text>
    </v-card>

    <!-- Appliquer une valeur à toutes les bornes qui ne l'ont pas, ou à celles de certaines valeurs -->
    <v-dialog v-model="apply.open" max-width="560">
      <v-card v-if="apply.open">
        <v-card-title>{{ $t('charging-point-tracked-keys.apply-title', {code}) }}</v-card-title>
        <v-card-text>
          <v-alert type="warning" text dense>{{ scopeText }}</v-alert>
          <!-- Un booléen : un interrupteur ; chaque borne le reçoit dans son écriture (True, TRUE, 1…) -->
          <v-switch v-if="boolean" :input-value="apply.value === 'true'" :label="$t('charging-point-tracked-keys.truth.' + (apply.value === 'true'))" inset dense hide-details class="mt-0" @change="value => apply.value = value ? 'true' : 'false'" />
          <v-text-field v-else v-model="apply.value" :label="$t('charging-point-tracked-keys.value')" outlined dense hide-details class="mono" />
          <v-checkbox v-if="!boolean && apply.value === ''" v-model="apply.allowEmpty" :label="$t('charging-point-tracked-keys.allow-empty')" dense hide-details class="mt-1" />
          <div class="mb-3" />
          <div class="text-caption text--secondary mb-1">{{ $t('charging-point-tracked-keys.apply-from') }}</div>
          <v-checkbox v-for="row in applicable" :key="row.id" v-model="apply.from" :value="row.id" dense hide-details class="mt-0">
            <template #label>
              <span class="mono">{{ row.values ? row.values.join(' / ') : display(row.value) }}</span>&nbsp;· {{ $tc('charging-point-tracked-keys.charging-points', row.chargingPoints - row.readonly, {count: row.chargingPoints - row.readonly}) }}
            </template>
          </v-checkbox>
          <v-alert type="info" text dense class="mt-4 mb-0">
            <v-progress-circular v-if="apply.counting" indeterminate size="16" class="mr-2" />
            {{ $tc('charging-point-tracked-keys.apply-summary', applyTotal, {count: applyTotal}) }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="apply.open = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :disabled="!applyTotal || (apply.value === '' && !apply.allowEmpty)" :loading="apply.saving" @click="createBatch">{{ $t('charging-point-tracked-keys.apply-confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import ChargingPointConfigurationRepository from "@repository/ChargingPointConfigurationRepository";
import {downloadCsv} from "@includes/csv";
import ChargingPointConfigurationValue from "./ChargingPointConfigurationValue";

/**
 * Une clé suivie sur le périmètre filtré : combien de bornes à chaque valeur (toutes marques, sous le nom que la clé
 * porte chez chacune), sans la clé, en écart ; un clic liste les bornes. « Appliquer une valeur » lance une
 * modification en masse sur les bornes qui ne l'ont pas.
 */
export default {
  props: {
    value: {type: Boolean, default: false},
    code: {type: String, default: null},
    filters: {type: Object, default: () => ({})},
  },

  data() {
    return {
      data: null,
      selected: null,
      list: null,
      loadingList: false,
      listOptions: {page: 1, itemsPerPage: 50},
      apply: {open: false, value: '', from: [], allowEmpty: false, count: null, counting: false, saving: false},
      valueSearch: '',
      exporting: false,
      countTimer: null,
    }
  },

  computed: {
    // Toutes ses valeurs sont des booléens : on les regroupe par sens, chaque borne garde son écriture.
    boolean() {
      return ChargingPointConfigurationValue.isBoolean(this.data.values.map(row => row.value))
    },
    valueRows() {
      const rows = this.data.values.map((row, index) => ({id: 'v' + index, ...row, missing: false, deviatingOnly: false}))
      if (!this.boolean) {
        return rows
      }
      return [true, false].map(truth => {
        const spellings = rows.filter(row => ChargingPointConfigurationValue.truth(row.value) === truth)
        return {
          id: 'truth-' + truth,
          value: String(truth),
          values: spellings.map(row => row.value),
          spellings,
          chargingPoints: spellings.reduce((sum, row) => sum + row.chargingPoints, 0),
          deviating: spellings.reduce((sum, row) => sum + row.deviating, 0),
          readonly: spellings.reduce((sum, row) => sum + row.readonly, 0),
          missing: false,
          deviatingOnly: false,
        }
      }).filter(row => row.spellings.length)
    },
    rows() {
      const rows = [...this.valueRows]
      if (this.data.missing) {
        rows.push({id: 'missing', value: null, chargingPoints: this.data.missing, deviating: 0, readonly: 0, missing: true})
      }
      if (this.data.deviating) {
        rows.push({id: 'deviating', value: null, chargingPoints: this.data.deviating, deviating: this.data.deviating, readonly: 0, missing: false, deviatingOnly: true})
      }
      return rows
    },
    // La recherche porte sur les valeurs (et leurs écritures) ; sans la clé et en écart restent affichés.
    shownRows() {
      const search = (this.valueSearch || '').trim().toLowerCase()
      if (!search) {
        return this.rows
      }
      return this.rows.filter(row => row.missing || row.deviatingOnly || (row.values || [row.value]).some(value => String(value ?? '').toLowerCase().includes(search)))
    },
    // Les valeurs qu'une modification en masse peut changer : celles qui diffèrent de la nouvelle, hors lecture seule.
    applicable() {
      return this.data ? this.rows.filter(row => !row.missing && !row.deviatingOnly && row.chargingPoints > row.readonly && !ChargingPointConfigurationValue.same(row.value, this.apply.value)) : []
    },
    // Le périmètre de la modification, rappelé avant de confirmer.
    scopeText() {
      const filters = Object.keys(this.filters).filter(name => this.filters[name])
      return filters.length
        ? this.$t('charging-point-tracked-keys.apply-scope-filtered', {filters: filters.map(name => this.$t('charging-point-configuration-batches.filters.' + name)).join(', ')})
        : this.$t('charging-point-tracked-keys.apply-scope-fleet')
    },
    // Le nombre exact du serveur dès qu'il est connu, l'estimation de la répartition en attendant.
    applyTotal() {
      return this.apply.count !== null ? this.apply.count : this.applyCount
    },
    applyCount() {
      return this.applicable.filter(row => this.apply.from.includes(row.id)).reduce((sum, row) => sum + row.chargingPoints - row.readonly, 0)
    },
    headers() {
      return [
        {text: this.$t('charging-point-fleet-configuration.charging-point'), value: 'name', sortable: false},
        {text: this.$t('charging-point-tracked-keys.value'), value: 'value', sortable: false},
        {text: this.$t('charging-point-fleet-configuration.organization'), value: 'organizationName', sortable: false},
        {text: this.$t('classification.manufacturer') + ' · ' + this.$t('classification.product-line'), value: 'type', sortable: false},
      ]
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.load()
      }
    },
    code() {
      if (this.value) {
        this.load()
      }
    },
    listOptions: {
      handler() {
        this.loadList()
      },
      deep: true,
    },
    // Une nouvelle valeur : on propose de changer toutes les autres.
    'apply.value'() {
      this.apply.from = this.applicable.map(row => row.id)
    },
    // Le nombre exact, compté par le serveur, à chaque changement du choix.
    'apply.from'() {
      this.countExactly()
    },
    'apply.allowEmpty'() {
      this.countExactly()
    },
  },

  methods: {
    load() {
      this.data = null
      this.selected = null
      this.list = null
      this.valueSearch = ''
      return ChargingPointConfigurationRepository.trackedKey(this.code, this.filters).then(data => {
        this.data = data
      }).catch(err => {
        this.notifyError(err)
      })
    },

    select(row) {
      this.selected = row
      this.list = null
      // Le changement de page (même à la première) relance la liste.
      this.listOptions = {...this.listOptions, page: 1}
    },

    loadList() {
      if (!this.selected) {
        return
      }
      const {page, itemsPerPage} = this.listOptions
      this.loadingList = true
      ChargingPointConfigurationRepository.trackedKeyChargingPoints(this.code, this.filters, {
        value: this.selected.value,
        values: this.selected.values || null,
        missing: this.selected.missing,
        deviating: this.selected.deviatingOnly,
        offset: (page - 1) * itemsPerPage,
        limit: itemsPerPage,
      }).then(list => {
        this.list = list
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loadingList = false
      })
    },

    openApply() {
      const expected = this.data.key.expected ?? ''
      const value = this.boolean ? String(ChargingPointConfigurationValue.truth(expected) !== false) : expected
      this.apply = {open: true, value, from: [], allowEmpty: false, count: null, counting: false, saving: false}
      this.$nextTick(() => {
        this.apply.from = this.applicable.map(row => row.id)
      })
    },

    batchPayload(dryRun) {
      const fromValues = this.applicable.filter(row => this.apply.from.includes(row.id)).flatMap(row => row.values || [row.value])
      return {code: this.code, value: this.apply.value, allowEmpty: this.apply.allowEmpty, fromValues, ...this.filters, dryRun}
    },

    countExactly() {
      clearTimeout(this.countTimer)
      this.apply.count = null
      if (!this.apply.open || !this.apply.from.length || (this.apply.value === '' && !this.apply.allowEmpty)) {
        return
      }
      this.countTimer = setTimeout(() => {
        this.apply.counting = true
        ChargingPointConfigurationRepository.createBatch(this.batchPayload(true)).then(({chargingPoints}) => {
          this.apply.count = chargingPoints
        }).catch(() => {
          this.apply.count = 0
        }).finally(() => {
          this.apply.counting = false
        })
      }, 300)
    },

    createBatch() {
      this.apply.saving = true
      ChargingPointConfigurationRepository.createBatch(this.batchPayload(false)).then(({id, chargingPoints}) => {
        this.apply.open = false
        this.notifySuccess('charging-point-tracked-keys.batch-created', {count: chargingPoints})
        this.$emit('batch-created', id)
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.apply.saving = false
      })
    },

    // Les bornes de la ligne choisie, par pages de 200.
    async exportCsv() {
      this.exporting = true
      try {
        const rows = []
        for (let offset = 0; offset < this.list.total; offset += 200) {
          const page = await ChargingPointConfigurationRepository.trackedKeyChargingPoints(this.code, this.filters, {
            value: this.selected.value, values: this.selected.values || null, missing: this.selected.missing, deviating: this.selected.deviatingOnly, offset, limit: 200,
          })
          rows.push(...page.rows)
        }
        downloadCsv(`${this.code}.csv`, [
          {text: this.$t('charging-point-fleet-configuration.charging-point'), value: row => row.name || row.identifier || row.chargingPointId},
          {text: this.$t('charging-point-fleet-configuration.organization'), value: 'organizationName'},
          {text: this.$t('charging-point-fleet-configuration.location'), value: 'locationName'},
          {text: this.$t('classification.manufacturer'), value: 'manufacturerName'},
          {text: this.$t('classification.product-line'), value: 'productLineName'},
          {text: this.$t('charging-point-tracked-keys.key-here'), value: 'key'},
          {text: this.$t('charging-point-tracked-keys.value'), value: row => row.present ? row.value : this.$t('charging-point-tracked-keys.missing')},
          {text: this.$t('charging-point-tracked-keys.expected'), value: 'expected'},
        ], rows)
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.exporting = false
      }
    },

    display(value) {
      return value === null ? '∅' : (value === '' ? '""' : value)
    },
  },
}
</script>

<style scoped lang="scss">
.mono {
  font-family: monospace;
}
.clickable {
  cursor: pointer;
}
.selected {
  background: rgba(0, 0, 0, 0.05);
}
</style>
